import { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@eriko/ui/src/components/ui/dialog';
import { Button } from '@eriko/ui/src/components/ui/button';
import { Input } from '@eriko/ui/src/components/ui/input';
import { Label } from '@eriko/ui/src/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@eriko/ui/src/components/ui/select';
import { useCreateContractor } from '../../../../hooks/useTax';

export function AddContractorDialog({ onClose }: { onClose: () => void }) {
  const createContractor = useCreateContractor();
  const [legalName, setLegalName] = useState('');
  const [tradingName, setTradingName] = useState('');
  const [tin, setTin] = useState('');
  const [tinType, setTinType] = useState<'SSN' | 'EIN' | ''>('');

  function handleSubmit() {
    createContractor.mutate(
      {
        legalName,
        tradingName: tradingName || undefined,
        tin: tin || undefined,
        tinType: tinType || undefined,
      },
      { onSuccess: () => onClose() },
    );
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add Contractor</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="legalName">Legal Name *</Label>
            <Input
              id="legalName"
              value={legalName}
              onChange={(e) => setLegalName(e.target.value)}
              placeholder="As shown on W-9"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="tradingName">Trading / DBA Name</Label>
            <Input
              id="tradingName"
              value={tradingName}
              onChange={(e) => setTradingName(e.target.value)}
              placeholder="Optional"
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="tin">TIN (SSN or EIN)</Label>
              <Input
                id="tin"
                value={tin}
                onChange={(e) => setTin(e.target.value)}
                placeholder={tinType === 'SSN' ? 'XXX-XX-XXXX' : tinType === 'EIN' ? 'XX-XXXXXXX' : 'XXX-XX-XXXX or XX-XXXXXXX'}
              />
              <p className="text-xs text-muted-foreground">
                {tinType === 'SSN' ? 'Social Security Number (XXX-XX-XXXX)' : tinType === 'EIN' ? 'Employer Identification Number (XX-XXXXXXX)' : 'Select TIN type for format guidance'}
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="tinType">TIN Type</Label>
              <Select value={tinType} onValueChange={(v) => setTinType(v as 'SSN' | 'EIN')}>
                <SelectTrigger id="tinType">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="SSN">SSN</SelectItem>
                  <SelectItem value="EIN">EIN</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {createContractor.isError && (
            <p className="text-sm text-red-500">Failed to add contractor. Please try again.</p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!legalName || createContractor.isPending}
          >
            {createContractor.isPending ? 'Adding...' : 'Add contractor'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
